import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { initials } from '../../utils/format';

/** Mono uppercase section rule above a block of content. */
export function SectionHeading({ children, sx }) {
  return (
    <Box
      sx={{
        m: '26px 0 12px',
        fontFamily: 'var(--mono)',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.1em',
        color: 'var(--tealdim)',
        textTransform: 'uppercase',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Icon + headline + one-line explanation. Three of these sit above most dashboards. */
export function InsightCard({ icon, title, text }) {
  return (
    <Paper
      sx={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        p: '13px 15px',
        display: 'flex',
        gap: 1.25,
        boxShadow: 'var(--shadow)',
        height: '100%',
      }}
    >
      <Box sx={{ fontSize: 16, flex: 'none' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontWeight: 650 }}>{title}</Box>
        <Box sx={{ mt: '2px', fontSize: 12.5, color: 'var(--ink-2)' }}>{text}</Box>
      </Box>
    </Paper>
  );
}

/** Three-up insight row on the 12-column grid. */
export function InsightRow({ items }) {
  return (
    <Grid container spacing={1.25} sx={{ mb: 2 }}>
      {items.map((it, i) => (
        <Grid key={i} size={{ xs: 12, md: 6, lg: 4 }}>
          <InsightCard {...it} />
        </Grid>
      ))}
    </Grid>
  );
}

/** Solid channel chip. */
export function ChannelChip({ color, children, small = false }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: small ? '7px' : '9px',
        py: small ? '1px' : '2px',
        borderRadius: '999px',
        fontSize: small ? 10 : 11,
        fontWeight: 600,
        color: '#fff',
        background: color,
      }}
    >
      {children}
    </Box>
  );
}

/** Outlined mono tag (lead source, role…). */
export function TagChip({ children, title }) {
  return (
    <Box
      component="span"
      title={title}
      sx={{
        fontFamily: 'var(--mono)',
        fontSize: 10,
        px: '8px',
        py: '2px',
        borderRadius: '6px',
        background: 'var(--card-2)',
        border: '1px solid var(--border)',
        color: 'var(--ink-2)',
        display: 'inline-block',
      }}
    >
      {children}
    </Box>
  );
}

/** Amber "known condition" pill, optionally removable. */
export function ConditionChip({ children, onRemove }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: '9px',
        py: '2px',
        borderRadius: '999px',
        fontSize: 10.5,
        fontWeight: 600,
        background: '#fbeee0',
        border: '1px solid #f0d0a8',
        color: '#8a5a12',
        m: '0 4px 4px 0',
      }}
    >
      {children}
      {onRemove && (
        <Box
          component="span"
          onClick={onRemove}
          sx={{ cursor: 'pointer', ml: '6px', color: '#b07a2a', fontWeight: 700 }}
        >
          ✕
        </Box>
      )}
    </Box>
  );
}

/** Green/grey active indicator used in the users table. */
export function StatusDot({ on }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontFamily: 'var(--mono)',
        fontSize: 10.5,
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        color: on ? 'var(--good-ink)' : 'var(--muted)',
      }}
    >
      <Box component="i" sx={{ width: 8, height: 8, borderRadius: '50%', background: on ? 'var(--mint)' : 'var(--muted)' }} />
      {on ? 'Active' : 'Disabled'}
    </Box>
  );
}

/** Small circular owner initials badge. */
export function OwnerAvatar({ name }) {
  if (!name) return null;
  return (
    <Box
      component="span"
      title={name}
      sx={{
        display: 'inline-grid',
        placeItems: 'center',
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: 'var(--side)',
        color: '#fff',
        fontFamily: 'var(--mono)',
        fontSize: 9,
        verticalAlign: 'middle',
        ml: '4px',
      }}
    >
      {initials(name)}
    </Box>
  );
}

/** Mono pill used for page counters and misc counts. */
export function Badge({ children, sx }) {
  return (
    <Box
      component="span"
      sx={{
        fontFamily: 'var(--mono)',
        fontSize: 11,
        px: '9px',
        py: '2px',
        borderRadius: '999px',
        background: 'var(--card-2)',
        border: '1px solid var(--border)',
        color: 'var(--ink-2)',
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Amber explainer panel (influencer tier note). */
export function NotePanel({ children }) {
  return (
    <Box
      sx={{
        background: '#fbf6e6',
        border: '1px solid #ecdca6',
        borderRadius: '12px',
        p: '14px 16px',
        fontSize: 13,
        color: '#6b5a1f',
        mb: 2,
        '& b': { color: '#5a4a12' },
      }}
    >
      {children}
    </Box>
  );
}

/** Muted footnote under a table or chart. */
export function FootNote({ children }) {
  return <Box sx={{ color: 'var(--muted)', fontSize: 12, mt: 1 }}>{children}</Box>;
}
